import React, { useState, useEffect, useRef } from 'react';
import styles from './Portfolio.module.css';

const Portfolio = () => {
    const [photos, setPhotos] = useState([]);
    const [settings, setSettings] = useState({
        columns: 3,
        gap: 20,
        showTitle: true,
        title: 'Портфолио'
    });
    const [loading, setLoading] = useState(true);
    const [selectedPhoto, setSelectedPhoto] = useState(null);
    const animatedRefs = useRef({});

    useEffect(() => {
        loadPortfolio();
    }, []);

    const loadPortfolio = async () => {
        try {
            const photosRes = await fetch('/api/content/portfolio?field=photos');
            const photosData = await photosRes.json();

            const settingsRes = await fetch('/api/content/portfolio?field=settings');
            const settingsData = await settingsRes.json();

            let allPhotos = [];
            if (photosData.data && Array.isArray(photosData.data)) {
                allPhotos = photosData.data;
            }

            allPhotos.sort((a, b) => (a.order || 0) - (b.order || 0));
            const visiblePhotos = allPhotos.filter(p => p.visible === true);

            setPhotos(visiblePhotos);
            setSettings(settingsData.data || { columns: 3, gap: 20, showTitle: true, title: 'Портфолио' });
        } catch (error) {
            console.error('Ошибка загрузки портфолио:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (loading || photos.length === 0) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const id = entry.target.dataset.id;
                        const element = animatedRefs.current[id];
                        if (element && !element.classList.contains(styles.animated)) {
                            element.classList.add(styles.animated);
                            // Отписываемся после анимации
                            observer.unobserve(entry.target);
                        }
                    }
                });
            },
            { threshold: 0.2, rootMargin: '0px 0px -50px 0px' }
        );

        // Наблюдаем за каждым элементом
        Object.values(animatedRefs.current).forEach((ref) => {
            if (ref && !ref.classList.contains(styles.animated)) {
                observer.observe(ref);
            }
        });

        return () => observer.disconnect();
    }, [loading, photos]);

    const openModal = (photo) => {
        setSelectedPhoto(photo);
    };

    const closeModal = () => {
        setSelectedPhoto(null);
    };

    if (loading) {
        return (
            <section id="portfolio" className={styles.portfolio}>
                <div className={styles.container}>
                    <div className={styles.loading}>Загрузка портфолио...</div>
                </div>
            </section>
        );
    }

    if (photos.length === 0) {
        return null;
    }

    return (
        <>
            <section id="portfolio" className={styles.portfolio}>
                <div className={styles.container}>
                    {settings.showTitle && (
                        <h2 className={styles.title}>{settings.title}</h2>
                    )}

                    <div
                        className={styles.grid}
                        style={{
                            display: 'grid',
                            gridTemplateColumns: `repeat(${settings.columns || 3}, 1fr)`,
                            gap: `${settings.gap || 20}px`
                        }}
                    >
                        {photos.map((photo, idx) => (
                            <div
                                key={photo.id}
                                ref={el => animatedRefs.current[photo.id] = el}
                                data-id={photo.id}
                                className={styles.gridItem}
                                onClick={() => openModal(photo)}
                                style={{ animationDelay: `${idx * 0.1}s` }}
                            >
                                <img
                                    src={`${photo.url}`}
                                    alt={`Работа мастера ${idx + 1}`}
                                    className={styles.image}
                                    loading="lazy"
                                />
                                <div className={styles.overlay}>
                                    <span className={styles.zoomIcon}>🔍</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Модальное окно */}
            {selectedPhoto && (
                <div className={styles.modal} onClick={closeModal}>
                    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <button className={styles.closeBtn} onClick={closeModal}>✕</button>
                        <img
                            src={`${selectedPhoto.url}`}
                            alt="Полный размер"
                            className={styles.modalImage}
                        />
                    </div>
                </div>
            )}
        </>
    );
};

export default Portfolio;